import { Menu, MenuItem, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import type { VyDate } from '@vy/utils';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMenu } from '../../../../hooks/use-menu';
import { Divider } from '../../../design/divider/Divider';

type VyRangePickerMenuProps = {
  fromDate: VyDate | null;
  toDate: VyDate | null;
  onStartDateChange: (startDate: VyDate | null) => void;
  onEndDateChange: (endDate: VyDate | null) => void;
  children?: React.ReactElement;
  disabled?: boolean;
};

export const VyRangePickerMenu: React.FC<VyRangePickerMenuProps> = ({
  fromDate,
  toDate,
  onStartDateChange,
  onEndDateChange,
  children,
  disabled = false,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();

  useEffect(() => {
    if (disabled) {
      handleMenuClose();
    }
  }, [disabled, handleMenuClose]);

  const handleRangeChange = (startDate: VyDate, endDate: VyDate) => {
    onStartDateChange?.(startDate);
    onEndDateChange?.(endDate);
    handleMenuClose();
  };

  const handleStartDateChange = (date: VyDate | null) => {
    onStartDateChange?.(date);
    handleMenuClose();
  };

  const handleEndDateChange = (date: VyDate | null) => {
    onEndDateChange?.(date);
    handleMenuClose();
  };

  return (
    <>
      {isNotNullOrUndefined(children) &&
        React.cloneElement(children, {
          onClick: disabled ? undefined : handleMenuOpen,
        })}
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 50,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 50,
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
      >
        <div className="px-7">
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            Select Range
          </Typography>
          <div className="mt-1 flex items-center justify-between gap-2">
            <DatePicker
              onChange={handleStartDateChange}
              value={fromDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: '180px',
                  },
                },
              }}
            />

            <div>-</div>

            <DatePicker
              onChange={handleEndDateChange}
              value={toDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: '180px',
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="px-7 pt-2">
          <Divider className="my-2" />
        </div>

        {menuItems.map((item) => {
          return (
            <MenuItem
              key={item.label}
              className={classNames(
                twMerge('flex cursor-pointer items-center py-1.5 px-7 gap-2 hover:bg-input'),
              )}
              onClick={() => handleRangeChange(item.from, item.to)}
            >
              {item.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const startOfDay = vyDate().startOf('day');
const endOfDay = vyDate().endOf('day');

const menuItems: {
  from: VyDate;
  to: VyDate;
  label: string;
}[] = [
  {
    from: startOfDay,
    to: endOfDay,
    label: 'Today',
  },
  {
    from: getLastDays(7),
    to: endOfDay,
    label: 'Last 7 days',
  },
  {
    from: getLastDays(30),
    to: endOfDay,
    label: 'Last 30 days',
  },
  {
    from: getLastMonths(6),
    to: endOfDay,
    label: 'Last 6 months',
  },
  {
    from: getLastYears(1),
    to: endOfDay,
    label: 'Last year',
  },
];

function getLastDays(days: number) {
  return startOfDay.subtract(days, 'days');
}

function getLastMonths(months: number) {
  return startOfDay.subtract(months, 'months');
}

function getLastYears(years: number) {
  return startOfDay.subtract(years, 'years');
}
